import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { loginWithPassword, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextAfter = new URLSearchParams(location.search).get("next") || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const user = await loginWithPassword(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${user.name?.split(" ")[0] || "friend"}!`);
      const roles = user?.roles || [];
      const mode = user?.current_mode || (roles.includes("owner") ? "owner" : "camper");
      navigate(nextAfter || (mode === "owner" ? "/owner" : "/passport"), { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Sign-in failed. Please try again.";
      toast.error(detail);
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to continue collecting stamps."
      footer={<>New here? <Link to="/register" className="font-bold" style={{ color: "var(--brand)" }} data-testid="link-register">Create an account</Link></>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="login-form">
        <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Email
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 px-4 py-3 rounded-xl border text-base font-normal normal-case tracking-normal"
            style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
            data-testid="login-email"
          />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Password
          <div className="relative mt-2">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 pr-11 rounded-xl border text-base font-normal normal-case tracking-normal"
              style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid="login-toggle-password"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>
        <div className="text-right -mt-2">
          <Link to="/forgot-password" className="text-xs font-bold" style={{ color: "var(--brand)" }} data-testid="link-forgot">
            Forgot password?
          </Link>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-green mt-2 justify-center w-full"
          data-testid="login-submit"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <><LogIn className="w-4 h-4" /> Sign in</>}
        </button>

        <div className="flex items-center gap-3 my-2 text-xs" style={{ color: "var(--on-surface-muted)" }}>
          <div className="flex-1 h-px" style={{ background: "var(--border-c)" }} />
          or continue with
          <div className="flex-1 h-px" style={{ background: "var(--border-c)" }} />
        </div>
        <button
          type="button"
          onClick={loginWithGoogle}
          className="btn justify-center w-full"
          style={{ background: "white", color: "#1F1F1F", border: "1px solid var(--border-c)" }}
          data-testid="login-google"
        >
          <img alt="" src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw" className="w-4 h-4" />
          Continue with Google
        </button>
      </form>
    </AuthShell>
  );
}
